/**
 * Socket.IO Client Composable
 *
 * Singleton Socket.IO connection shared across all composables.
 * Connects to the HQ server using the same origin and session cookie.
 */

import { ref, computed } from 'vue'
import { io, Socket } from 'socket.io-client'

type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error'

// Module-level singleton state
const socket = ref<Socket | null>(null)
const connectionState = ref<ConnectionState>('disconnected')
let initialized = false

function initSocket() {
  if (initialized) return

  const s = io({
    path: '/socket.io',
    // Prefer polling first, upgrade to websocket if available
    // This prevents console spam when WebSocket upgrades fail behind reverse proxies
    transports: ['polling', 'websocket'],
    withCredentials: true,
    autoConnect: false,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
  })

  s.on('connect', () => {
    connectionState.value = 'connected'
  })

  s.on('disconnect', () => {
    connectionState.value = 'disconnected'
  })

  s.on('connect_error', () => {
    connectionState.value = 'error'
  })

  s.io.on('reconnect_attempt', () => {
    connectionState.value = 'connecting'
  })

  socket.value = s
  initialized = true
}

export function useSocket() {
  initSocket()

  const isConnected = computed(() => connectionState.value === 'connected')

  function connect() {
    if (socket.value && !socket.value.connected) {
      connectionState.value = 'connecting'
      socket.value.connect()
    }
  }

  function disconnect() {
    if (socket.value) {
      socket.value.disconnect()
    }
  }

  function on(event: string, callback: (..._args: any[]) => void) { // any[] required: Socket.IO callback contravariance
    socket.value?.on(event, callback)
  }

  function off(event: string, callback?: (..._args: any[]) => void) { // any[] required: Socket.IO callback contravariance
    if (callback) {
      socket.value?.off(event, callback)
    } else {
      socket.value?.removeAllListeners(event)
    }
  }

  function emit(event: string, data?: Record<string, unknown>) {
    socket.value?.emit(event, data)
  }

  return {
    socket,
    connectionState,
    isConnected,
    connect,
    disconnect,
    on,
    off,
    emit,
  }
}
